import { AuthUserInfo, CtwaAdsResponse, CtwaChannelTemplatesResponse } from 'src/index'

export type AiConfigRequest = {
    adminChannel: {
        group: string
        phone: string
        aiConfig?: ChannelAiConfigDraft
    }
    ignoreCache: boolean
    aiTrainingFiles: boolean
}

export type ChannelAiConfigDraft = {
    aiAssitantAvaliable: boolean
    active: boolean
    aiAudioTranscriptionAvaliable: boolean
    aiSelfSurveyAvaliable?: boolean
    aiConversationAvailable?: boolean
    aiConversationV2Available?: boolean
    aiSelfScheduleConversationAvailable?: boolean
    ignoreQuestionCustomerStep?: boolean
    assistantUrl: string
    naturalAssistantUrl: string
    channel: string
    extractNameSchemaId?: string
    extractVehicleKm?: string
    extractVehiclePlate?: string
    extractCanBack?: string
    selectNameSchemaId?: string
    selectCompanySchemaId?: string
    selectCpfSchemaId?: string
    selectGenericSchemaId?: string
    extractCpfCnpjSchemaId?: string
    flows: FlowAiConfig[]
    templateFlows: TemplateFlowAiConfig[]
    status?: AiConfigStatus
    trainingFiles: AiTrainingFile[]
    customContext?: string
    generalTrainingFlows?: FlowAiConfig[]
    aiNaturalSchemaId?: string
    webSearch?: AiWebSearch
    naturalFlowsProcessed?: boolean
    /**
     * menuUUIDs com a IA desligada pelo menu pai — a etapa MENU do Bot Builder
     * ou um menu agrupador (SYO-68147).
     *
     * Complementa `flows[].flows[0].active`, que continua sendo a ativação
     * individual do menu: a IA só vale quando o menu está ativo E não está
     * nesta lista. É uma lista separada de propósito — gravar a desativação do
     * pai dentro de `flows[]` faria o `saveMenus` atribuir `aiFlowId` a um menu
     * agrupador, e o `next()` do syo-bot-alice testa `aiFlowId` antes de
     * `listSubService`, o que quebraria a navegação de submenu.
     *
     * Lista vazia/ausente = comportamento anterior à história, inalterado.
     */
    aiDisabledMenus?: string[]
}

export type AiWebSearch = {
    active: boolean
    urls: string[]
}

export type AiTrainingFile = {
    id: string
    companies: number[]
    name: string
    url: string
    type: 'PDF' | 'TXT' | 'DOC' | 'DOCX'
    size: number // em bytes
    processed?: boolean
    error?: boolean
}

export type TemplateFlowAiConfig = {
    id?: string
    schemaId?: string
    key: string
    flows: TemplateFlowSchema[]
}

export type TemplateFlowSchema = {
    name?: string
    active?: boolean
    description?: string
    domain?: string
    actionType?: TemplateActionType
    changeTicketOptions?: ChangeTicketOptions
    event?: TemplateEvent
    schema: DetailSchema
}

export type ChangeTicketOptions = {
    type: ChangeTicketOptionsType
    result: ChangeTicketResult
}

export enum ChangeTicketResult {
    VERY_HOT = 'VERY_HOT',
    HOT = 'HOT',
    WARM = 'WARM',
    COLD = 'COLD',
    VERY_COLD = 'VERY_COLD'
}

export enum ResultTemperatureLabel {
    VERY_HOT = 'MUITO QUENTE',
    HOT = 'QUENTE',
    WARM = 'MORNO',
    COLD = 'FRIO',
    VERY_COLD = 'MUITO FRIO'
}

export enum ChangeTicketOptionsType {
    TEMPERATURE = 'TEMPERATURE'
}

export enum TemplateActionType {
    EVENT = 'EVENT',
    ROBOT = 'ROBOT',
    EVENT_ACTION = 'EVENT_ACTION'
}

export type TemplateEvent = {
    enablesEventGeneration: boolean
    group: string
    type: string
}

export type FlowAiConfig = {
    schemaId?: string
    templateId?: string
    menuId?: string
    flows: FlowSchema[]
}

export type FlowEvent = {
    type: string
    group: string
    enablesEventGeneration: boolean
}

export type FlowSchema = {
    name?: string
    active?: boolean
    description?: string
    domain?: string
    schema: DetailSchema
    event?: FlowEvent
}

export type DetailSchema = {
    questions: FlowQuestion[]
}

export type AttachedFile = {
    active: boolean
    url: string
    type: 'PDF' | 'JPG' | 'PNG' | 'PDF' | 'MP4'
    size: number // em bytes
    silent?: boolean
    message?: string // maximo 250 caracteres, so tem efeito quando silent for true
}

type CtwaRetargeting = {
    templateId: string
    templateText: string
    value: number
}

type CtwaAdIdRetargeting = {
    externalAdId: string
    retargetings: CtwaRetargeting
}

export type QuestionDependency = {
    questionId: string
    value?: string
}

export type FlowQuestion = {
    questionType?: 'CONVERSATION' | 'TRAINING'
    attachedFile?: AttachedFile
    question?: string
    useInterfaceNegotiation?: boolean
    type?: string
    description?: string
    required?: boolean
    enum?: string[]
    _key?: string
    id?: string
    dependency?: QuestionDependency
    interface?: {
        fieldId: number
        label: string
        mask: string | null
        options: Option[]
        fieldType: 'TEXT' | 'SELECT'
        fieldInterest: string
        brand?: boolean
        model?: boolean
        version?: boolean
        year?: boolean
        color?: boolean
    }
    usesCustomRetargeting?: boolean
    ctwaRetargeting?: CtwaAdIdRetargeting[]
}

export type PublishDraftAiConfig = {
    group: string
    channel: string
    aiConfig: ChannelAiConfigDraft
}

export type RemoveChannelAiConfig = {
    group: string
    channel: string
}

export type IaAvaliableRequest = {
    group: string
    channel: string
    iaAvaliable: boolean
}

export type Option = {
    option: string
}

export type Field = {
    label: string
    options: Option[]
    fieldInterest: string
    fieldType: 'TEXT' | 'SELECT'
    id: number
    mask: string | null
    dependsOnId?: number
    dependsOn?: string
    brand?: boolean
    model?: boolean
    version?: boolean
    year?: boolean
    color?: boolean
}

export type InterfaceItem = {
    companies: number[]
    name: string
    id: number
    fields: Field[]
}

export type InterfaceErrorList = {
    companies: number[]
    name: string
    id: number
}[]

export type InterfaceError = {
    message: string
    interfaces?: InterfaceErrorList
}

export type InterfaceList = InterfaceItem[]

export type ResponseInterface = {
    interface?: InterfaceItem
    error?: InterfaceError
}

export type ResponseFunnel = {
    id?: number
    label?: string
}

export type ResponseFunnelStep = {
    id?: number
    label?: string
}

type AiConfigStatus = {
    type: 'success' | 'error' | 'warning'
    errors?: AiConfigStatusError[] | AiConfigTrainingFileError[]
    warning?: {
        naturalFlowsProcessed?: boolean
    }
}

export type AiConfigStatusError = {
    questionKey: string
    invalidType: boolean
    invalidSize: boolean
    invalidMessageLength?: boolean
}

export type AiConfigTrainingFileError = {
    fileId: string
    invalidType?: boolean
    invalidSize?: boolean
    invalidAmountFiles?: boolean
    emptyFile?: boolean
}

export type SynchronizeAiTrainingFilesRequest = {
    user: AuthUserInfo
    channel: string
    tab: string
    aiTrainingFiles?: boolean
}

export type SynchronizeAiTrainingFilesParams = {
    channel: string
    group: string
}

export type CtwaRetargetingOptions = {
    templates: CtwaChannelTemplatesResponse[]
    ads: CtwaAdsResponse[]
}

export type NaturalAiProcessedFlowsDto = {
    processed: boolean
}
